
import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

interface NavbarProps {
  onReferenceClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onReferenceClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (targetId: string) => {
    const element = document.getElementById(targetId);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { name: 'VISÃO', id: 'vision' },
    { name: 'MÓDULOS', id: 'modules' },
    { name: 'PÚBLICO', id: 'audience' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ${isScrolled ? 'bg-black/90 backdrop-blur-md border-b border-zinc-800 py-3' : 'bg-transparent py-6'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <button onClick={scrollToTop} className="focus:outline-none transition-transform active:scale-95" aria-label="Voltar ao topo">
              <img 
                src="https://legislativo.pro/img/logo_guruvoto.gif" 
                alt="Guru.voto" 
                className="h-12 sm:h-16 mr-4"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <button 
                key={link.name}
                onClick={() => handleNavClick(link.id)}
                className="text-zinc-400 hover:text-white transition-colors text-xs font-mono uppercase tracking-widest focus:outline-none"
              >
                {link.name}
              </button>
            ))}
            <button 
              onClick={onReferenceClick}
              className="text-zinc-400 hover:text-white transition-colors text-xs font-mono uppercase tracking-widest focus:outline-none"
            >
              REFERÊNCIA
            </button>
            <button 
              onClick={() => handleNavClick('waitlist')}
              className="bg-gradient-to-r from-[#c17c0c] to-[#a66a0a] hover:from-[#e08e0e] hover:to-[#c17c0c] text-white px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all shadow-[0_0_15px_rgba(193,124,12,0.3)] hover:shadow-[0_0_25px_rgba(193,124,12,0.5)] focus:outline-none"
            >
              SOLICITAR ACESSO
            </button>
          </div>

          <div className="md:hidden">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
              className="text-zinc-400 p-2 focus:outline-none"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-black border-b border-zinc-800 py-8 px-6 flex flex-col space-y-6 items-center animate-in fade-in slide-in-from-top-4 duration-300">
          {navLinks.map((link) => (
            <button 
              key={link.name}
              onClick={() => handleNavClick(link.id)} 
              className="text-zinc-400 text-sm font-mono uppercase tracking-widest hover:text-white focus:outline-none"
            >
              {link.name}
            </button>
          ))}
          <button 
            onClick={() => {
              onReferenceClick?.();
              setIsMobileMenuOpen(false);
            }} 
            className="text-zinc-400 text-sm font-mono uppercase tracking-widest hover:text-white focus:outline-none"
          >
            REFERÊNCIA
          </button>
          <button 
            onClick={() => handleNavClick('waitlist')} 
            className="w-full text-center bg-gradient-to-r from-[#c17c0c] to-[#a66a0a] hover:from-[#e08e0e] hover:to-[#c17c0c] text-white px-5 py-4 rounded-full text-sm font-bold uppercase tracking-widest transition-all shadow-lg shadow-[#c17c0c]/20 hover:shadow-[#c17c0c]/40 focus:outline-none"
          >
            SOLICITAR ACESSO
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
